<template>
  <div v-bind="attributes.top" class="c-bitts-input">
    <slot name="label">
      <BittsFormLabel
        v-if="formLabel"
        :label="formLabel"
        :disabled="disabled"
        :for="inputId"
      >
        <template #secondaryText>
          <slot name="secondaryText" />
        </template>
      </BittsFormLabel>
    </slot>
    <Input
      v-bind="attributes.input"
      ref="bittsInput"
      :id="inputId"
      :value="modelValue"
      :disabled="disabled"
      :placeholder="placeholder"
      :name="name"
      :type="inputType as any"
      :allow-clear="type === 'search'"
      class="c-bitts-input__input"
      :readonly="readOnly"
      :class="{
        danger: status === 'danger',
        success: status === 'success',
        disabled: disabled,
        'read-only': readOnly,
        [size]: true,
        expandable: expandOnFocus,
        'has-input': expandOnFocus && !!modelValue.length,
      }"
      @input="
        (e) => emit('update:modelValue', (e.target as HTMLInputElement).value)
      "
      @keydown="handleKeydown"
    >
      <template #prefix>
        <div
          v-if="!!$slots.prefix || type === 'search'"
          class="c-bitts-input__prefix"
        >
          <slot name="prefix">
            <FontAwesomeIcon
              v-if="type === 'search'"
              :icon="['fak', 'search']"
              class="text-info-accent mr-4 cursor-pointer"
              :class="{ 'text-neutral-text-placeholder': disabled }"
              @click="(e: MouseEvent) => emit('enter-pressed', e)"
            />
          </slot>
        </div>
      </template>
      <template #suffix>
        <div
          v-if="!!$slots.suffix || type === 'password' || allowCopy"
          class="c-bitts-input__suffix"
        >
          <slot name="suffix">
            <span v-if="type === 'password'">
              <FontAwesomeIcon
                :icon="['fak', inputType === 'password' ? 'hidden' : 'visible']"
                class="text-neutral-text-placeholder cursor-pointer"
                @click="onSwitchVisiblity"
              />
            </span>
            <span
              v-if="allowCopy"
              class="input-copy-popover"
              @click="copyToClipBoard(modelValue)"
            >
              <FontAwesomeIcon
                :icon="hasCopied ? ['fas', 'check'] : ['fak', 'copy']"
                class="text-neutral-text-placeholder cursor-pointer"
              />
            </span>
          </slot>
        </div>
      </template>
    </Input>
    <BittsStatus
      v-if="status !== 'default'"
      :status="status"
      :disabled="disabled"
      :success-text="successText"
      :danger-text="dangerText"
    />
    <div v-if="caption" class="c-bitts-input__caption">
      {{ caption }}
    </div>
  </div>
</template>

<script lang="ts">
import { StrictExclude } from '@crossbeam/types';

import { Input } from 'ant-design-vue';
import { computed, onBeforeUnmount, ref, useAttrs, useId } from 'vue';

import BittsFormLabel from '../BittsFormLabel/BittsFormLabel.vue';
import { FormLabelType } from '../BittsFormLabel/types';
import BittsStatus, {
  BittsStatusDefaults,
  BittsStatusProps,
} from '../BittsStatus/BittsStatus.vue';
import { BittsSize } from '../types';

export interface BittsInputProps extends BittsStatusProps {
  name: string;
  expandOnFocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  type?: 'text' | 'search' | 'password';
  modelValue?: string;
  // eslint-disable-next-line vue/no-unused-properties
  focus?: boolean;
  placeholder?: string;
  formLabel?: FormLabelType | string;
  caption?: string;
  size?: StrictExclude<BittsSize, 'x-small' | 'large' | 'x-large' | 'tiny'>;
  allowCopy?: boolean;
}
</script>
<script setup lang="ts">
const props = withDefaults(defineProps<BittsInputProps>(), {
  readOnly: false,
  expandOnFocus: false,
  type: 'text',
  focus: false,
  modelValue: '',
  placeholder: '',
  formLabel: '',
  caption: '',
  size: 'medium',
  allowCopy: false,
  ...BittsStatusDefaults,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'enter-pressed': [event: KeyboardEvent | MouseEvent];
  keydown: [event: KeyboardEvent];
}>();

/* This let's us bind only the class to the topmost div by default, and
the data-testid will be bound to the input. This makes styling and
testing way easier. */
defineOptions({ inheritAttrs: false });
const rawAttrs = useAttrs();
const generatedId = useId();
const inputId = computed(
  () => (rawAttrs.id as string | undefined) ?? generatedId,
);
const attributes = computed(() => {
  const { class: className, onKeydown: _, id: _id, ...rest } = rawAttrs;
  return { top: { class: className }, input: { ...rest } };
});

function handleKeydown(e: KeyboardEvent) {
  emit('keydown', e);
  if (e.key === 'Enter') {
    emit('enter-pressed', e);
  }
}

const hasCopied = ref(false);
let copyTimer: ReturnType<typeof setTimeout> | null = null;

const copyToClipBoard = async (textToCopy: string) => {
  if (!navigator.clipboard) {
    copyTextFallback(textToCopy);
  } else {
    await navigator.clipboard.writeText(textToCopy);
  }
  hasCopied.value = true;
  if (copyTimer) clearTimeout(copyTimer);
  copyTimer = setTimeout(() => {
    hasCopied.value = false;
  }, 1500);
};

onBeforeUnmount(() => {
  if (copyTimer) clearTimeout(copyTimer);
});

/* Helper for copyToClipBoard */
const copyTextFallback = (textToCopy: string) => {
  const textArea = document.createElement('textarea');
  textArea.value = textToCopy;

  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  document.execCommand('copy');
  document.body.removeChild(textArea);
};
/* Password visiblity toggle w/ our custom icon */
const inputType = ref(props.type === 'password' ? 'password' : 'text');
function onSwitchVisiblity() {
  inputType.value = inputType.value === 'text' ? 'password' : 'text';
}

const bittsInput = ref<typeof Input | null>(null);

function focusInput() {
  bittsInput.value?.focus();
}

defineExpose({
  focusInput,
});
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.c-bitts-input {
  @apply w-full;
  .expandable {
    &.ant-input-affix-wrapper {
      @apply overflow-hidden;
      transition: width 0.5s;
    }
  }
  .expandable:not(:hover):not(.has-input):not(:focus-within) {
    &.ant-input-affix-wrapper {
      @apply w-[40px] shadow-none;
      border-color: transparent;
    }
  }
  .expandable:hover,
  .expandable.has-input,
  .expandable:focus-within {
    &.ant-input-affix-wrapper {
      @apply w-[200px];
    }
  }

  .c-bitts-input__input {
    @apply rounded-8 border-neutral-border shadow-component
    outline  w-full py-8 text-neutral-text outline-transparent;
    font-family: inherit;

    &.small {
      @apply py-4;
    }

    .ant-input {
      font-family: inherit;
    }

    .ant-input-clear-icon {
      @apply flex items-center;
    }

    .ant-input::placeholder {
      @apply text-neutral-text-placeholder;
    }

    &.success:not(.disabled) {
      @apply outline-success-accent border-success-accent outline;
    }

    &.danger:not(.disabled) {
      @apply border-danger-accent outline-danger-accent outline;
    }

    &.disabled:not(.read-only) {
      @apply bg-neutral-background-disabled outline-neutral-border border-neutral-border outline-0;
      &:hover,
      &:focus {
        @apply outline-0;
      }
    }

    &.read-only {
      @apply opacity-100 pointer-events-auto;
      &:not(.success, .danger) {
        &:hover,
        &:focus-within {
          @apply border border-neutral-border-focus outline-neutral-border-focus border-solid outline;
        }
      }
    }
  }

  .c-bitts-input__prefix,
  .c-bitts-input__suffix {
    @apply flex gap-4;
  }

  .input-copy-popover {
    @apply flex justify-center mx-4 cursor-pointer;
  }
}

.c-bitts-input__caption {
  @apply text-left text-neutral-500 text-sm mt-4;
}

.input-popover.bitts-overflow-menu.ant-popover {
  @apply z-1035;
  .ant-popover-inner {
    @apply text-sm px-8 py-4 bg-neutral-background-tooltip rounded-bts-md opacity-90 border-none;
  }
}

/* Ant-D has very particular border styles we have to override */
span.c-bitts-input__input.ant-input-affix-wrapper:not(
    .ant-input-affix-wrapper-disabled,
    .disabled,
    .danger,
    .success
  ) {
  &:hover,
  &:focus-within {
    @apply border-neutral-border-focus outline-neutral-border-focus outline shadow-none border-solid;
  }
}
</style>
